// src/lib/peopleService.ts
//
// BekalNanti — Milestone P3.6: People & Profile Management Service
// Interacts with canonical schema: bekalnanti_app (tables: persons, profiles)
// Provides complete lifecycle (Active / Archived) without destructive data loss.

import { supabase } from './supabase'
import { PersonRecord, ProfileRecord } from '../journey/types'

export interface RelationshipOption {
  value: string
  label: string
  englishLabel: string
  description: string
  isSelf?: boolean
  allowsMultiple: boolean
}

export const CANONICAL_RELATIONSHIPS: RelationshipOption[] = [
  {
    value: 'self',
    label: 'Saya (Pengelola Rencana)',
    englishLabel: 'Self',
    description: 'Pembuat dan pengelola utama rencana keluarga.',
    isSelf: true,
    allowsMultiple: false,
  },
  {
    value: 'spouse',
    label: 'Pasangan (Suami / Istri)',
    englishLabel: 'Spouse / Partner',
    description: 'Pasangan hidup untuk perencanaan bersama.',
    allowsMultiple: true,
  },
  {
    value: 'child',
    label: 'Anak',
    englishLabel: 'Child',
    description: 'Anak kandung, anak asuh, atau generasi penerus.',
    allowsMultiple: true,
  },
  {
    value: 'parent',
    label: 'Orang Tua (Ayah / Ibu)',
    englishLabel: 'Parent',
    description: 'Ayah, ibu, atau mertua.',
    allowsMultiple: true,
  },
  {
    value: 'sibling',
    label: 'Saudara Kandung',
    englishLabel: 'Sibling',
    description: 'Kakak atau adik kandung.',
    allowsMultiple: true,
  },
  {
    value: 'dependent',
    label: 'Tanggungan Lain',
    englishLabel: 'Dependent',
    description: 'Anggota keluarga yang membutuhkan dukungan nafkah/biaya.',
    allowsMultiple: true,
  },
  {
    value: 'family',
    label: 'Keluarga Besar',
    englishLabel: 'Extended Family',
    description: 'Keponakan, sepupu, kakek, nenek, atau kerabat dekat.',
    allowsMultiple: true,
  },
  {
    value: 'friend',
    label: 'Teman / Rekan',
    englishLabel: 'Friend / Partner',
    description: 'Sahabat atau rekan proyek masa depan.',
    allowsMultiple: true,
  },
  {
    value: 'other',
    label: 'Lainnya',
    englishLabel: 'Other',
    description: 'Penerima manfaat lainnya.',
    allowsMultiple: true,
  },
]

export function formatRelationship(relationship: string): string {
  const match = CANONICAL_RELATIONSHIPS.find((r) => r.value.toLowerCase() === relationship.toLowerCase())
  if (match) return match.label
  // Fallbacks for legacy/common inputs
  switch (relationship.toLowerCase()) {
    case 'self':
      return 'Saya'
    case 'child':
      return 'Anak'
    case 'spouse':
      return 'Pasangan'
    case 'parent':
      return 'Orang Tua'
    case 'sibling':
      return 'Saudara'
    case 'dependent':
      return 'Tanggungan'
    case 'family':
      return 'Keluarga'
    case 'friend':
      return 'Teman'
    case 'household':
      return 'Keluarga Bersama'
    default:
      return relationship
  }
}

export function isPersonSelf(person: PersonRecord): boolean {
  return person.relationship === 'self'
}

export interface NewPersonInput {
  fullName: string
  relationship: string
  dateOfBirth?: string | null
}

export interface UpdatePersonInput {
  fullName?: string
  relationship?: string
  dateOfBirth?: string | null
  status?: 'active' | 'archived'
}

/**
 * Fetch all persons in a household with optional archived inclusion.
 */
export async function fetchHouseholdPersons(
  householdId: string,
  includeArchived = false
): Promise<PersonRecord[]> {
  let query = supabase
    .from('persons')
    .select('id, full_name, relationship, status, date_of_birth, created_at, updated_at')
    .eq('household_id', householdId)
    .order('created_at', { ascending: true })

  if (!includeArchived) {
    query = query.eq('status', 'active')
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Gagal memuat daftar anggota keluarga: ${error.message}`)
  }

  return (data || []).map((p: any) => ({
    id: p.id,
    fullName: p.full_name,
    relationship: p.relationship,
    status: (p.status as 'active' | 'archived') || 'active',
    dateOfBirth: p.date_of_birth || null,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  }))
}

/**
 * Add a new person to a household.
 */
export async function addPerson(
  householdId: string,
  input: NewPersonInput
): Promise<PersonRecord> {
  const trimmedName = input.fullName.trim()
  if (!trimmedName) {
    throw new Error('Nama lengkap tidak boleh kosong.')
  }
  const rel = input.relationship.trim().toLowerCase()
  if (!rel) {
    throw new Error('Hubungan keluarga harus dipilih.')
  }

  const { data, error } = await supabase
    .from('persons')
    .insert({
      household_id: householdId,
      full_name: trimmedName,
      relationship: rel,
      date_of_birth: input.dateOfBirth || null,
      status: 'active',
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal menambahkan anggota keluarga: ${error.message}`)
  }

  return {
    id: data.id,
    fullName: data.full_name,
    relationship: data.relationship,
    status: (data.status as 'active' | 'archived') || 'active',
    dateOfBirth: data.date_of_birth || null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

/**
 * Update an existing person in a household.
 */
export async function updatePerson(
  personId: string,
  updates: UpdatePersonInput
): Promise<PersonRecord> {
  const payload: any = {
    updated_at: new Date().toISOString(),
  }

  if (updates.fullName !== undefined) {
    const trimmed = updates.fullName.trim()
    if (!trimmed) throw new Error('Nama tidak boleh kosong.')
    payload.full_name = trimmed
  }
  if (updates.relationship !== undefined) {
    const trimmedRel = updates.relationship.trim().toLowerCase()
    if (!trimmedRel) throw new Error('Hubungan keluarga tidak boleh kosong.')
    payload.relationship = trimmedRel
  }
  if (updates.dateOfBirth !== undefined) {
    payload.date_of_birth = updates.dateOfBirth || null
  }
  if (updates.status !== undefined) {
    payload.status = updates.status
  }

  const { data, error } = await supabase
    .from('persons')
    .update(payload)
    .eq('id', personId)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal memperbarui data keluarga: ${error.message}`)
  }

  return {
    id: data.id,
    fullName: data.full_name,
    relationship: data.relationship,
    status: (data.status as 'active' | 'archived') || 'active',
    dateOfBirth: data.date_of_birth || null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

/**
 * Archive a person non-destructively.
 * Ensures historical goals and calculations linked to this person remain intact.
 */
export async function archivePerson(personId: string): Promise<PersonRecord> {
  return updatePerson(personId, { status: 'archived' })
}

/**
 * Unarchive / Restore a previously archived person.
 */
export async function unarchivePerson(personId: string): Promise<PersonRecord> {
  return updatePerson(personId, { status: 'active' })
}

/**
 * Update the authenticated user's profile.
 */
export async function updateUserProfile(
  profileId: string,
  updates: {
    displayName: string
    preferredLanguage?: string
    defaultCurrency?: string
  }
): Promise<ProfileRecord> {
  const trimmed = updates.displayName.trim()
  if (!trimmed) {
    throw new Error('Nama tampilan tidak boleh kosong.')
  }

  const payload: any = {
    display_name: trimmed,
    updated_at: new Date().toISOString(),
  }
  if (updates.preferredLanguage) payload.preferred_language = updates.preferredLanguage
  if (updates.defaultCurrency) payload.default_currency = updates.defaultCurrency

  const { data, error } = await supabase
    .from('profiles')
    .update(payload)
    .eq('id', profileId)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal memperbarui profil pengguna: ${error.message}`)
  }

  // Update local cache
  if (data.auth_user_id) {
    setCachedProfileName(data.auth_user_id, trimmed)
  }

  // Synchronize auth user metadata for seamless multi-context persistence
  if (supabase?.auth && typeof supabase.auth.updateUser === 'function') {
    try {
      await supabase.auth.updateUser({
        data: { display_name: trimmed },
      })
    } catch (authErr) {
      console.warn('Could not sync display_name to auth metadata:', authErr)
    }
  }

  return {
    id: data.id,
    displayName: data.display_name,
    preferredLanguage: data.preferred_language,
    defaultCurrency: data.default_currency,
  }
}

const PROFILE_CACHE_PREFIX = 'bekalnanti_profile_name_'

/**
 * Get cached display name from localStorage for instant zero-flash UX.
 */
export function getCachedProfileName(userId: string): string | null {
  try {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(`${PROFILE_CACHE_PREFIX}${userId}`)
  } catch {
    return null
  }
}

/**
 * Cache display name in localStorage.
 */
export function setCachedProfileName(userId: string, name: string): void {
  try {
    if (typeof window === 'undefined') return
    localStorage.setItem(`${PROFILE_CACHE_PREFIX}${userId}`, name)
  } catch {}
}

/**
 * Clear cached display name upon logout.
 */
export function clearCachedProfileName(userId: string): void {
  try {
    if (typeof window === 'undefined') return
    localStorage.removeItem(`${PROFILE_CACHE_PREFIX}${userId}`)
  } catch {}
}

/**
 * Fetch canonical profile directly by auth_user_id from bekalnanti_app.profiles.
 * Guaranteed single source of truth for user profile identity.
 */
export async function getCanonicalProfile(userId: string): Promise<ProfileRecord | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, preferred_language, default_currency')
    .eq('auth_user_id', userId)
    .maybeSingle()

  if (error || !data) return null
  return {
    id: data.id,
    displayName: data.display_name,
    preferredLanguage: data.preferred_language,
    defaultCurrency: data.default_currency,
  }
}

/**
 * Strict priority fallback resolver:
 * 1. canonical profiles.display_name
 * 2. auth user metadata display_name (ONLY if canonical profile is genuinely unavailable)
 * 3. email prefix fallback
 */
export function resolveDisplayName(
  profileDisplayName?: string | null,
  authMetadataDisplayName?: string | null,
  email?: string | null
): string {
  if (profileDisplayName && profileDisplayName.trim().length > 0) {
    return profileDisplayName.trim()
  }
  if (authMetadataDisplayName && authMetadataDisplayName.trim().length > 0) {
    return authMetadataDisplayName.trim()
  }
  if (email && email.trim().length > 0) {
    return email.split('@')[0] || 'Pengelola'
  }
  return 'Pengelola'
}

/**
 * Update household planning space name independently.
 * Persists to bekalnanti_app.households.name.
 * Does NOT alter profile display_name, person full_name, or goal assignments.
 */
export async function updateHouseholdName(
  householdId: string,
  newName: string
): Promise<string> {
  const trimmed = newName.trim()
  if (!trimmed) {
    throw new Error('Nama ruang perencanaan tidak boleh kosong.')
  }
  if (trimmed.length < 2) {
    throw new Error('Nama ruang perencanaan minimal 2 karakter.')
  }
  if (trimmed.length > 60) {
    throw new Error('Nama ruang perencanaan maksimal 60 karakter.')
  }

  const { error } = await supabase
    .from('households')
    .update({
      name: trimmed,
      updated_at: new Date().toISOString(),
    })
    .eq('id', householdId)

  if (error) {
    throw new Error(`Gagal memperbarui nama ruang perencanaan: ${error.message}`)
  }

  return trimmed
}

/**
 * One-time reconciliation for existing households with legacy generated names
 * (e.g. "Keluarga Burner Mail" or "Keluarga Pengguna BekalNanti") using the current profile name.
 * Does NOT create a permanent coupling between profile display name and household name.
 */
export async function reconcileLegacyHouseholdName(
  householdId: string,
  currentHouseholdName: string,
  profileDisplayName?: string | null
): Promise<string> {
  if (!currentHouseholdName) return currentHouseholdName

  const isLegacyBurner =
    currentHouseholdName.toLowerCase().includes('burner mail') ||
    currentHouseholdName.toLowerCase().includes('pengguna bekalnanti')

  const cleanProfileName = (profileDisplayName || '').trim()
  const isProfileValid =
    cleanProfileName.length > 0 &&
    !cleanProfileName.toLowerCase().includes('burner') &&
    !cleanProfileName.toLowerCase().includes('pengguna bekalnanti')

  if (isLegacyBurner && isProfileValid) {
    const reconciled = `Keluarga ${cleanProfileName}`
    try {
      await supabase
        .from('households')
        .update({
          name: reconciled,
          updated_at: new Date().toISOString(),
        })
        .eq('id', householdId)
      return reconciled
    } catch (err) {
      console.warn('Could not reconcile legacy household name:', err)
      return currentHouseholdName
    }
  }

  return currentHouseholdName
}

